import { useCursorPagination } from '@frameio/graphql-client';
import {
  type GetWorkspacesBasicQuery,
  useGetWorkspacesListQuery,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { usePaginatedWorkspacesLegacy } from '@frameio/workspaces/shared/hooks';

const WORKSPACE_PAGE_COUNT = 5;

/**
 * Hook that provides paginated workspace list data with infinite scroll
 * capabilities. Fetches workspaces for the active account and handles
 * pagination automatically when scrolling near the end of the list.
 *
 * @returns An object containing workspace data, loading state, and scroll
 * handler
 * @returns workspacesData - The paginated workspace data from the GraphQL query
 * @returns loading - Boolean indicating if the initial query is loading
 * @returns handleFetchNextOnScroll - Callback to trigger next page fetch on
 * scroll (boundary: 70%)
 */
export const usePaginatedWorkspaceListData = () => {
  const accountId = useActiveAccountId();

  const {
    data: workspacesData,
    loading,
    fetchNext,
  } = useCursorPagination(
    useGetWorkspacesListQuery({
      variables: {
        accountId,
        page: {
          first: WORKSPACE_PAGE_COUNT,
        },
      },
      skip: !accountId,
    })
  );

  const { handleFetchNextOnScroll } = usePaginatedWorkspacesLegacy({
    data: workspacesData as GetWorkspacesBasicQuery,
    loading,
    fetchNext,
    fetchCount: WORKSPACE_PAGE_COUNT,
    fetchBoundaryMultiplier: 0.7,
  });

  return {
    workspacesData,
    loading,
    handleFetchNextOnScroll,
  };
};
